import fetch from "./sessionFetch";
import {authHeaders, baseUrl, handleError, handleResponse} from "./apiUtils";
import { getStoredAuthEpoch, getStoredAuthIdentity, persistAuthSession, persistAuthToken } from "./authTokenStorage";
import { authenticationRecoveryError, credentialRequest, flushPendingLogout, withSessionLock } from "./sessionCoordinator";

async function parseJsonBody(response) {
  const rawResponse = await response.text();
  if (!rawResponse) {
    return null;
  }

  try {
    return JSON.parse(rawResponse);
  } catch (error) {
    return rawResponse;
  }
}

function finishAuthResponse(response) {
  if (!response || !response.token) {
    return response;
  }

  persistAuthSession(response.session);
  persistAuthToken(response.token);
  return response.user;
}

function authenticatedLogin(path, payload, google = false) {
  return withSessionLock(async () => {
    await flushPendingLogout();
    const identity = getStoredAuthIdentity();
    const epoch = getStoredAuthEpoch();
    const { response, result } = await credentialRequest(path, { method: "POST", body: JSON.stringify(payload) }, async received => ({
      response: received,
      result: google ? await parseJsonBody(received) : await handleResponse(received),
    }));
    if (google && !response.ok) throw new Error(typeof result === "string" ? result : `Network response was not ok. status:${response.status}`);
    if (getStoredAuthIdentity() !== identity || getStoredAuthEpoch() !== epoch) {
      if (result && result.session) await credentialRequest("session/logout", { method: "POST", body: JSON.stringify({ sessionId: result.session.id }) });
      throw authenticationRecoveryError("Session changed");
    }
    return finishAuthResponse(result);
  });
}

function signIn(user) {
  return authenticatedLogin("login", user).catch(handleError);
}

async function signInWithGoogle(token) {
  return authenticatedLogin("login/google", { token }, true);
}

function signUpWithPassword(payload) {
  return fetch(`${baseUrl}signup/password`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(payload),
  })
    .then(handleResponse)
    .catch(handleError);
}

function confirmEmailVerification(token) {
  return fetch(`${baseUrl}email-verification/confirm`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ token }),
  })
    .then(handleResponse)
    .catch(handleError);
}

function resendEmailVerification(email) {
  return fetch(`${baseUrl}email-verification/resend`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ email }),
  })
    .then(handleResponse)
    .catch(handleError);
}

function selectLoginContext(contextChoiceToken, contextKey) {
  return authenticatedLogin("login/context", { contextChoiceToken, contextKey }).catch(handleError);
}

function selectSuperAdminSchool(schoolChoiceToken, schoolId) {
  return authenticatedLogin("login/school-context", { schoolChoiceToken, schoolId }).catch(handleError);
}

function getLoginContexts() {
  return fetch(`${baseUrl}login/contexts`, {
    method: "POST",
    headers: authHeaders({ "content-type": "application/json" }),
    body: JSON.stringify({}),
  })
    .then(handleResponse)
    .catch(handleError);
}

function completeSsoProfile(profileCompletionToken, details) {
  return authenticatedLogin("signup/sso/complete-profile", { profileCompletionToken, ...details }).catch(handleError);
}

function requestPasswordReset(email) {
  return fetch(`${baseUrl}password-reset/request`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ email }),
  })
    .then(handleResponse)
    .catch(handleError);
}

function confirmPasswordReset(token, password) {
  return fetch(`${baseUrl}password-reset/confirm`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ token, password }),
  })
    .then(handleResponse)
    .catch(handleError);
}

export default {
  signIn,
  signInWithGoogle,
  signUpWithPassword,
  confirmEmailVerification,
  resendEmailVerification,
  selectLoginContext,
  selectSuperAdminSchool,
  getLoginContexts,
  completeSsoProfile,
  requestPasswordReset,
  confirmPasswordReset,
};
